import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, Trash2, ArrowRight, Mail, Phone } from "lucide-react";
import { AvatarBubble } from "@/components/AvatarUploader";

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  if (user.role !== "admin") {
    return <div className="text-slate-500">Nur für Admin / Chef sichtbar.</div>;
  }

  const filtered = list.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [u.name, u.email, u.department, u.position].some((v) => (v || "").toLowerCase().includes(s));
  });

  const del = async (id) => {
    if (!window.confirm("Mitarbeiter wirklich deaktivieren?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Mitarbeiter deaktiviert");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="ph-label">Team</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Mitarbeiter</h1>
          <p className="text-slate-500 text-sm mt-1">Lege neue Mitarbeiter an, bearbeite Stammdaten und Rollen.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
            <Input
              data-testid="employees-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suchen…"
              className="pl-9 h-10 w-64"
            />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-employee" className="bg-kauz-navy hover:bg-kauz-navy-dark h-10">
                <Plus className="h-4 w-4 mr-2" /> Neuer Mitarbeiter
              </Button>
            </DialogTrigger>
            <NewEmployeeDialog onCreated={() => { load(); setOpen(false); }} />
          </Dialog>
        </div>
      </div>

      <div className="ph-card overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-200 bg-slate-50/50">
          <div className="col-span-4">Name</div>
          <div className="col-span-3">Position</div>
          <div className="col-span-3">Abteilung</div>
          <div className="col-span-1">Rolle</div>
          <div className="col-span-1 text-right">Aktion</div>
        </div>
        <ul className="divide-y divide-slate-100">
          {filtered.map((u) => (
            <li key={u.id} className="grid grid-cols-1 md:grid-cols-12 items-center px-5 py-4 hover:bg-slate-50/60 transition gap-2">
              <div className="md:col-span-4 flex items-center gap-3">
                <AvatarBubble user={u} size="md" />
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 truncate">
                    <Mail className="h-3 w-3" /> {u.email}
                  </div>
                </div>
              </div>
              <div className="md:col-span-3 text-sm text-slate-700">{u.position || "—"}</div>
              <div className="md:col-span-3 text-sm text-slate-700">{u.department || "—"}</div>
              <div className="md:col-span-1">
                <Badge className={u.role === "admin" ? "bg-sky-700" : "bg-slate-200 text-slate-700 hover:bg-slate-200"}>
                  {u.role === "admin" ? "Admin" : "MA"}
                </Badge>
              </div>
              <div className="md:col-span-1 flex items-center gap-1 md:justify-end">
                <Link to={`/mitarbeiter/${u.id}`} data-testid={`employee-open-${u.id}`}>
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                {u.id !== user.id && (
                  <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700" onClick={() => del(u.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-5 py-12 text-center text-slate-400 text-sm">Keine Mitarbeiter gefunden.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

function NewEmployeeDialog({ onCreated }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "", role: "employee",
    position: "", department: "", phone: "", vacation_days_per_year: 30,
  });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", { ...form, vacation_days_per_year: Number(form.vacation_days_per_year) || 30 });
      toast.success("Mitarbeiter angelegt");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-display">Neuer Mitarbeiter</DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required>
            <Input data-testid="new-emp-name" value={form.name} onChange={(e) => update("name", e.target.value)} required />
          </Field>
          <Field label="E-Mail" required>
            <Input data-testid="new-emp-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Passwort" required>
            <Input data-testid="new-emp-password" type="text" value={form.password} onChange={(e) => update("password", e.target.value)} required minLength={6} />
          </Field>
          <Field label="Rolle">
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger data-testid="new-emp-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="employee">Mitarbeiter</SelectItem>
                <SelectItem value="admin">Chef / Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Position">
            <Input value={form.position} onChange={(e) => update("position", e.target.value)} />
          </Field>
          <Field label="Abteilung">
            <Input value={form.department} onChange={(e) => update("department", e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Telefon">
            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} />
          </Field>
          <Field label="Urlaubstage pro Jahr">
            <Input type="number" min={0} max={60} value={form.vacation_days_per_year} onChange={(e) => update("vacation_days_per_year", e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button type="submit" data-testid="new-emp-submit" disabled={busy} className="bg-kauz-navy hover:bg-kauz-navy-dark">
            {busy ? "Anlegen…" : "Mitarbeiter anlegen"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
        {label}{required && <span className="text-red-600"> *</span>}
      </Label>
      {children}
    </div>
  );
}
